import {
  Toolbar,
  IconButton,
  Stack,
  Button,
  Chip,
  InputBase,
  Typography,
  type SxProps,
  type Theme,
  Box,
} from '@mui/material'
import { type SVGProps } from 'react'

import { type ToolbarTableBulkImportProps, type BulkActionType } from './types'

import { ReactComponent as SearchIcon } from '@/assets/icons/discover-icon.svg'
import { CardActivityType } from '@/models/card-activity/card-activity.model'
import { AIGenerateIcon } from '@/views/NewBuilder/components/IconsBuilder'

export default function ToolbarTableBulkImport({
  numSelected,
  searchValue,
  onSearchChange,
  cardType,
  onToggleBulkModals,
}: ToolbarTableBulkImportProps) {
  const bulkActions: {
    label: string
    icon: JSX.Element | null
    type: BulkActionType
    isGradient?: boolean
  }[] = [
    {
      label: 'Edit',
      icon: <EditIcon />,
      type: 'bulk_edit',
    },
    {
      label: 'Add Images',
      icon: <AddImageIcon />,
      type: 'add_images',
    },
    ...getCardTypeSpecificActions(cardType),
    {
      label: 'Delete',
      icon: null,
      type: 'bulk_delete',
    },
  ]

  return (
    <Toolbar
      sx={{
        p: '16px 20px',
        background: 'transparent',
        minHeight: 'unset',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" gap={2}>
        {/* Left: Always show Customize label and chip if selected */}
        <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
          <Typography fontSize={16} fontWeight={600} color="#101828">
            Customize
          </Typography>
          {numSelected > 0 && (
            <Chip
              color="primary"
              label={`${numSelected.toString()} selected`}
              variant="outlined"
              sx={{
                borderRadius: '9999px',
                border: '1px solid #FCCEEE',
                background: '#FDF2FA',
                fontSize: '12px',
                fontWeight: '500',
                color: '#C11574',
                minHeight: '22px',
                height: '22px',
                '& .MuiChip-label': {
                  padding: '0 10px',
                  lineHeight: '20px',
                },
              }}
            />
          )}
        </Stack>
        {/* Right: Search bar, and if selected, actions to the right of search */}
        <Stack direction="row" alignItems="center" gap={2}>
          <Box minWidth={320} maxWidth={350} width="100%" display="flex" justifyContent="flex-end">
            <SearchBar searchValue={searchValue} onSearchChange={onSearchChange} />
          </Box>
          {numSelected > 0 && (
            <Stack alignItems="center" direction="row" flexWrap="wrap" gap="8px" justifyContent="flex-end">
              {bulkActions.map(({ label, icon, type, isGradient }) =>
                isGradient ? (
                  <GradientOutlineButton
                    key={type}
                    icon={icon}
                    label={label}
                    sx={{ p: '8px 12px' }}
                    onClick={() => {
                      onToggleBulkModals({ open: true, type })
                    }}
                  />
                ) : (
                  <Button
                    key={type}
                    color={type === 'bulk_delete' ? 'error' : 'secondary'}
                    startIcon={icon}
                    variant={type === 'bulk_delete' ? 'contained' :"outlined"}
                    onClick={() => {
                      onToggleBulkModals({ open: true, type })
                    }}
                  >
                    {label}
                  </Button>
                ),
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Toolbar>
  )
}

function SearchBar({
  searchValue,
  onSearchChange,
}: {
  searchValue: string
  onSearchChange: (newValue: string) => void
}) {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(event.target.value)
  }

  return (
    <InputBase
      fullWidth
      placeholder="Search"
      startAdornment={
        <IconButton size="small">
          <SearchIcon height={20} width={20} />
        </IconButton>
      }
      sx={{
        height: '44px',
        fontSize: '16px',
        padding: '10px 14px',
        borderRadius: '16px',
        width: 360,
        border: '1px solid #D0D5DD',
        background: '#FFF',
        '&& input': {
          color: 'text.secondary',
          '&::placeholder': {
            color: 'text.disabled',
            opacity: 1,
          },
        },
      }}
      value={searchValue}
      onChange={handleChange}
    />
  )
}

function getCardTypeSpecificActions(
  cardType: CardActivityType,
): { label: string; icon: JSX.Element | null; type: BulkActionType; isGradient?: boolean }[] {
  switch (cardType) {
    case CardActivityType.READ_REPEAT:
      return [
        {
          label: 'Generate Translations',
          icon: <AIGenerateIcon />,
          type: 'generate_translations',
          isGradient: true,
        },
      ]
    case CardActivityType.READ_RESPOND:
    case CardActivityType.RESPOND_WRITE:
      return [
        {
          label: 'AI Grading Method',
          icon: <AIGenerateIcon />,
          type: 'select_grading_method',
          isGradient: true,
        },
      ]
    case CardActivityType.MULTIPLE_CHOICE:
      return [
        {
          label: 'Generate Options',
          icon: <AIGenerateIcon />,
          type: 'generate_options',
          isGradient: true,
        },
      ]
    case CardActivityType.SHORT_ANSWER:
      return [
        {
          label: 'Generate Correct Answers',
          icon: <AIGenerateIcon />,
          type: 'generate_correct_answers',
          isGradient: true,
        },
      ]
    default:
      return []
  }
}

interface GradientOutlineButtonProps {
  label: string
  disabled?: boolean
  onClick: () => void
  sx?: SxProps<Theme>
  icon?: JSX.Element | null
}

export function GradientOutlineButton({
  label = 'Generate',
  disabled = false,
  onClick,
  sx,
}: GradientOutlineButtonProps) {
  return (
    <Button
      disabled={disabled}
      startIcon={<AIGenerateIcon />}
      sx={{
        position: 'relative',
        borderRadius: '12px',
        background: '#FFF',
        border: '1px solid transparent',
        backgroundClip: 'padding-box',
        opacity: disabled ? 0.4 : 1,
        height: 36,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-1px',
          left: '-1px',
          right: '-1px',
          bottom: '-1px',
          borderRadius: '16px',
          padding: '1px',
          background: 'linear-gradient(45deg, #FF6C6C, #DD7BFF)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'destination-out',
          maskComposite: 'exclude',
          pointerEvents: 'none',
        },
        ...sx,
      }}
      onClick={onClick}
    >
      <Typography
        fontWeight={500}
        sx={{
          textTransform: 'none',
          backgroundColor: '#fff',
          color: 'transparent',
          backgroundImage: 'linear-gradient(45deg, #FF6C6C 0%, #DD7BFF 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        {label}
      </Typography>
    </Button>
  )
}

function EditIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={20} viewBox="0 0 20 20" width={20} xmlns="http://www.w3.org/2000/svg" {...props}>
      <g clipPath="url(#a)">
        <path
          d="M9.167 3.334h-3.5c-1.4 0-2.1 0-2.635.273a2.5 2.5 0 0 0-1.093 1.092c-.272.535-.272 1.235-.272 2.635v7c0 1.4 0 2.1.272 2.635a2.5 2.5 0 0 0 1.093 1.093c.535.272 1.235.272 2.635.272h7c1.4 0 2.1 0 2.635-.272a2.5 2.5 0 0 0 1.093-1.093c.272-.535.272-1.235.272-2.635v-3.5m-10 2.5h1.395c.408 0 .612 0 .804-.046q.256-.061.481-.2c.169-.102.313-.247.601-.535l7.969-7.969a1.768 1.768 0 0 0-2.5-2.5l-7.969 7.97c-.288.287-.432.431-.535.6q-.139.224-.2.482c-.046.191-.046.395-.046.803z"
          stroke="#3E4784"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.667}
        />
      </g>
      <defs>
        <clipPath id="a">
          <path d="M0 0h20v20H0z" fill="#fff" />
        </clipPath>
      </defs>
    </svg>
  )
}

function AddImageIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={20} viewBox="0 0 20 20" width={20} xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M10.417 2.501H6.5c-1.4 0-2.1 0-2.635.273a2.5 2.5 0 0 0-1.093 1.092C2.5 4.401 2.5 5.101 2.5 6.501v7c0 1.4 0 2.1.272 2.635a2.5 2.5 0 0 0 1.093 1.093C4.4 17.5 5.1 17.5 6.5 17.5h7.667c.775 0 1.162 0 1.48-.085a2.5 2.5 0 0 0 1.768-1.768c.085-.318.085-.705.085-1.48m-1.667-7.5v-5m-2.5 2.5h5M8.75 7.085a1.667 1.667 0 1 1-3.333 0 1.667 1.667 0 0 1 3.333 0m3.742 2.848-7.05 6.408c-.396.36-.594.541-.612.697a.42.42 0 0 0 .14.36c.117.103.385.103.921.103h7.822c1.2 0 1.8 0 2.27-.201a2.5 2.5 0 0 0 1.316-1.315c.201-.471.201-1.071.201-2.27 0-.404 0-.606-.044-.794a1.7 1.7 0 0 0-.312-.648c-.119-.152-.276-.278-.591-.53L14.222 9.88c-.316-.253-.473-.379-.647-.423a.83.83 0 0 0-.465.014c-.17.056-.32.191-.618.463"
        stroke="#3E4784"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.667}
      />
    </svg>
  )
}
